import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import {
    ArrowLeft,
    Mail,
    Globe,
    Calendar,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Link as LinkIcon,
    TrendingUp,
    DollarSign,
    MousePointer,
} from 'lucide-react';
import { format } from 'date-fns';

type SocialMedia = string[];

interface AffiliateLink {
    id: string;
    shortCode: string;
    productId?: string;
    storeId?: string;
    clicks: number;
    conversions: number;
    earnings: number;
    createdAt: string;
    isActive: boolean;
}

interface Commission {
    id: string;
    amount: number;
    status: 'PENDING' | 'APPROVED' | 'PAID';
    orderId: string;
    createdAt: string;
}

interface Affiliate {
    id: string;
    userId: string;
    displayName: string;
    bio?: string;
    phoneNumber?: string;
    socialMedia?: SocialMedia;
    paymentPhone?: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
    totalClicks?: number;
    totalSales?: number;
    totalEarnings?: number;
    pendingEarnings?: number;
    paidEarnings?: number;
    commissionRate?: number;
    createdAt: string;
    approvedAt?: string;
    rejectionReason?: string;
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
    };
    links?: AffiliateLink[];
    commissions?: Commission[];
}

const statusConfig = {
    PENDING: {
        label: 'Pending',
        icon: Clock,
        className: 'bg-gray-100 text-gray-800 border-gray-300',
    },
    APPROVED: {
        label: 'Approved',
        icon: CheckCircle,
        className: 'bg-gray-200 text-gray-900 border-gray-400',
    },
    REJECTED: {
        label: 'Rejected',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200',
    },
    SUSPENDED: {
        label: 'Suspended',
        icon: AlertCircle,
        className: 'bg-white text-gray-600 border-gray-300',
    },
};



export default function AffiliateDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAffiliateDetails();
    }, [id]);

    const fetchAffiliateDetails = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await apiClient.get<Affiliate>(`/affiliate/admin/${id}`);
            setAffiliate(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load affiliate details');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        try {
            await apiClient.patch(`/affiliate/admin/${id}/approve`);
            await fetchAffiliateDetails();
        } catch (err: any) {
            alert(err.message || 'Failed to approve affiliate');
        }
    };

    const handleReject = async () => {
        const reason = prompt('Please enter rejection reason:');
        if (!reason) return;

        try {
            await apiClient.patch(`/affiliate/admin/${id}/reject`, { rejectionReason: reason });
            await fetchAffiliateDetails();
        } catch (err: any) {
            alert(err.message || 'Failed to reject affiliate');
        }
    };

    const handleSuspend = async () => {
        if (!confirm('Are you sure you want to suspend this affiliate?')) return;

        try {
            await apiClient.patch(`/affiliate/admin/${id}/suspend`);
            await fetchAffiliateDetails();
        } catch (err: any) {
            alert(err.message || 'Failed to suspend affiliate');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
                    <p className="text-gray-600 font-medium">Loading affiliate details...</p>
                </div>
            </div>
        );
    }

    if (error || !affiliate) {
        return (
            <div className="p-8">
                <div className="bg-white border border-gray-200 rounded-2xl p-8">
                    <div className="text-center">
                        <XCircle className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                        <h2 className="text-xl font-black text-black mb-2">Failed to Load Affiliate</h2>
                        <p className="text-gray-600">{error}</p>
                        <button
                            onClick={() => navigate('/affiliates')}
                            className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            Back to Affiliates
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const StatusIcon = (statusConfig as any)[affiliate.status]?.icon || AlertCircle;
    const conversionRate = (affiliate.totalClicks || 0) > 0
        ? (((affiliate.totalSales || 0) / (affiliate.totalClicks || 1)) * 100).toFixed(2)
        : '0.00';

    return (
        <div className="p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/affiliates')}
                        className="inline-flex items-center justify-center h-10 px-4 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </button>
                    <div>
                        <h1 className="text-3xl font-black tracking-tight text-black">
                            {affiliate.displayName}
                        </h1>
                        <p className="text-gray-500 mt-1">{affiliate.user.email}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {(affiliate.status === 'PENDING' || affiliate.status === 'SUSPENDED') && (
                        <>
                            <button
                                onClick={handleApprove}
                                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                {affiliate.status === 'SUSPENDED' ? 'Reactivate' : 'Approve'}
                            </button>
                            <button
                                onClick={handleReject}
                                className="px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Reject
                            </button>
                        </>
                    )}
                    {affiliate.status === 'APPROVED' && (
                        <button
                            onClick={handleSuspend}
                            className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-500 transition-colors"
                        >
                            Suspend
                        </button>
                    )}
                </div>
            </div>

            {/* Profile Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
                <div className="flex items-start gap-6">
                    <div className="w-24 h-24 bg-gray-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <span className="text-4xl font-black text-white">
                            {affiliate.displayName.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                            <h2 className="text-2xl font-black text-black">{affiliate.displayName}</h2>
                            <span className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${statusConfig[affiliate.status].className}`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig[affiliate.status].label}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span>{affiliate.user.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>Joined {format(new Date(affiliate.createdAt), 'MMMM d, yyyy')}</span>
                            </div>
                            {affiliate.phoneNumber && (
                                <div className="flex items-center gap-2 text-gray-700">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <span>WhatsApp: {affiliate.phoneNumber}</span>
                                </div>
                            )}
                            {affiliate.paymentPhone && (
                                <div className="flex items-center gap-2 text-gray-700">
                                    <DollarSign className="w-4 h-4 text-gray-400" />
                                    <span>Pesa Number: {affiliate.paymentPhone}</span>
                                </div>
                            )}
                            {affiliate.approvedAt && (
                                <div className="flex items-center gap-2 text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-gray-400" />
                                    <span>Approved {format(new Date(affiliate.approvedAt), 'MMM d, yyyy')}</span>
                                </div>
                            )}
                        </div>

                        {affiliate.bio && (
                            <div className="mt-4">
                                <h3 className="text-sm font-bold text-black mb-2">Bio</h3>
                                <p className="text-sm text-gray-600">{affiliate.bio}</p>
                            </div>
                        )}

                        {affiliate.socialMedia && Array.isArray(affiliate.socialMedia) && affiliate.socialMedia.length > 0 && (
                            <div className="mt-4">
                                <h3 className="text-sm font-bold text-black mb-3">Promotional Channels</h3>
                                <div className="space-y-2">
                                    {affiliate.socialMedia.map((url, index) => {
                                        if (!url) return null;
                                        return (
                                            <a
                                                key={index}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                                            >
                                                <Globe className="w-4 h-4 text-gray-400 group-hover:text-black" />
                                                <span className="text-sm font-medium text-gray-700 group-hover:text-black truncate">
                                                    {url}
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Clicks</h3>
                        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                            <MousePointer className="w-5 h-5 text-gray-700" />
                        </div>
                    </div>
                    <p className="text-3xl font-black text-black">{(affiliate.totalClicks || 0).toLocaleString()}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Sales</h3>
                        <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                    </div>
                    <p className="text-3xl font-black text-black">{(affiliate.totalSales || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{conversionRate}% conversion</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Earnings</h3>
                        <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                        </div>
                    </div>
                    <p className="text-3xl font-black text-black">KES {(affiliate.totalEarnings || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">Commission: {affiliate.commissionRate || 0}%</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pending Payout</h3>
                        <div className="w-10 h-10 bg-gray-700 rounded-xl flex items-center justify-center">
                            <Clock className="w-5 h-5 text-white" />
                        </div>
                    </div>
                    <p className="text-3xl font-black text-black">KES {(affiliate.pendingEarnings || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">Paid: KES {(affiliate.paidEarnings || 0).toLocaleString()}</p>
                </div>
            </div>

            {/* Affiliate Links */}
            {affiliate.links && affiliate.links.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-8">
                    <h2 className="text-xl font-black text-black mb-6">Affiliate Links</h2>
                    <div className="space-y-4">
                        {affiliate.links.map((link) => (
                            <div key={link.id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <LinkIcon className="w-4 h-4 text-gray-400" />
                                        <code className="text-sm font-mono text-gray-900">/{link.shortCode}</code>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${link.isActive ? 'bg-gray-200 text-gray-900' : 'bg-gray-50 text-gray-500'}`}>
                                            {link.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        {format(new Date(link.createdAt), 'MMM d, yyyy')}
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Clicks:</span>
                                        <span className="ml-2 font-medium text-gray-900">{link.clicks}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Conversions:</span>
                                        <span className="ml-2 font-medium text-gray-900">{link.conversions}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Earnings:</span>
                                        <span className="ml-2 font-medium text-gray-900">KES {(link.earnings || 0).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Rejection Reason */}
            {affiliate.status === 'REJECTED' && affiliate.rejectionReason && (
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                    <h3 className="text-sm font-bold text-black mb-2">Rejection Reason</h3>
                    <p className="text-sm text-gray-700">{affiliate.rejectionReason}</p>
                </div>
            )}
        </div>
    );
}
